<script lang="ts" setup>
import { computed } from 'vue';

type Props = {
  isPlaying: boolean;
  size?: number;
  barWidth?: number;
  gap?: number;
  color?: string;
  count?: number;
};

const props = withDefaults(defineProps<Props>(), {
  size: 24,
  barWidth: 2,
  gap: 2,
  color: 'rgb(var(--v-theme-on-surface))',
  count: 3,
});

const animationClass = computed(() => (props.isPlaying ? 'playing' : 'paused'));

const dynamicStyles = computed(() => ({
  '--indicator-size': `${props.size}px`,
  '--bar-width': `${props.barWidth}px`,
  '--gap': `${props.gap}px`,
  '--bar-color': props.color,
}));
</script>

<template>
  <div class="audio-wave-indicator" :class="animationClass" :style="dynamicStyles">
    <div class="bar bar-1"></div>
    <div class="bar bar-2"></div>
    <div class="bar bar-3"></div>
  </div>
</template>

<style lang="scss" scoped>
.audio-wave-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;

  // Use CSS Variables
  gap: var(--gap);
  height: var(--indicator-size);
  width: var(--indicator-size);
  padding: 4px;
}

.bar {
  width: var(--bar-width);
  height: 100%;
  background: var(--bar-color);
  border-radius: 1px;
  transform-origin: bottom;
  transform: scaleY(0.3);
}

.bar-1 {
  animation: wave1 1.1s ease-in-out infinite;
}
.bar-2 {
  animation: wave2 0.9s ease-in-out infinite;
}
.bar-3 {
  animation: wave3 1.3s ease-in-out infinite;
}

.playing .bar {
  animation-play-state: running;
}

.paused .bar {
  animation: fadeToRest 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

@keyframes wave1 {
  0%,
  100% {
    transform: scaleY(0.4);
  }
  25% {
    transform: scaleY(0.9);
  }
  50% {
    transform: scaleY(0.5);
  }
  75% {
    transform: scaleY(1);
  }
}

@keyframes wave2 {
  0%,
  100% {
    transform: scaleY(0.6);
  }
  30% {
    transform: scaleY(1);
  }
  60% {
    transform: scaleY(0.3);
  }
  85% {
    transform: scaleY(0.8);
  }
}

@keyframes wave3 {
  0%,
  100% {
    transform: scaleY(0.5);
  }
  20% {
    transform: scaleY(0.3);
  }
  55% {
    transform: scaleY(0.95);
  }
  80% {
    transform: scaleY(0.6);
  }
}

@keyframes fadeToRest {
  to {
    transform: scaleY(0.3);
  }
}
</style>
